import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

// Cognito configuration
const poolData = {
  UserPoolId: process.env.REACT_APP_USER_POOL_ID || 'us-east-1_VNBka4PnD',
  ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '2nlc38ng6pvru753pa77cv2o8b',
};

const userPool = new CognitoUserPool(poolData);

export interface AuthUser {
  email: string;
  userId: string;
  groups?: string[];
  role?: 'admin' | 'guest';
}

export interface LoginResult {
  user: AuthUser;
  token: string;
}

export interface PasswordChangeResult {
  requiresPasswordChange: boolean;
  cognitoUser?: any;
  userAttributes?: any;
}

export class AuthService {
  static async login(email: string, password: string): Promise<LoginResult> {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          // Use ID token instead of access token - ID token contains email and user attributes
          const idToken = result.getIdToken();
          const payload = idToken.payload;
          
          // Extract user groups from JWT token
          const groups = payload['cognito:groups'] as string[] || [];
          
          const authUser: AuthUser = {
            email: payload.email || email,
            userId: payload.sub,
            groups,
            role: (groups.includes('admin') || groups.includes('admins')) ? 'admin' : 'guest',
          };

          // Store user info in localStorage for persistence
          localStorage.setItem('authToken', idToken.getJwtToken());
          localStorage.setItem('authUser', JSON.stringify(authUser));

          resolve({
            user: authUser,
            token: idToken.getJwtToken(),
          });
        },
        onFailure: (error: any) => {
          console.error('Login error:', error);
          
          // Handle specific Cognito errors
          if (error.code === 'NotAuthorizedException') {
            reject(new Error('Invalid email or password'));
          } else if (error.code === 'UserNotConfirmedException') {
            reject(new Error('Please verify your email address'));
          } else if (error.code === 'PasswordResetRequiredException') {
            reject(new Error('Password reset required'));
          } else if (error.code === 'UserNotFoundException') {
            reject(new Error('User not found'));
          } else if (error.code === 'TooManyRequestsException') {
            reject(new Error('Too many login attempts. Please try again later.'));
          } else {
            reject(new Error(error.message || 'Login failed'));
          }
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // Store the cognitoUser and userAttributes for password change
          const passwordChangeResult: PasswordChangeResult = {
            requiresPasswordChange: true,
            cognitoUser,
            userAttributes
          };
          reject(passwordChangeResult);
        },
      });
    });
  }

  static async logout(): Promise<void> {
    try {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local storage even if logout fails
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
    }
  }

  static async getCurrentUser(): Promise<AuthUser | null> {
    return new Promise((resolve) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        // Fallback to localStorage if no active Cognito session
        const storedUser = localStorage.getItem('authUser');
        const storedToken = localStorage.getItem('authToken');
        
        if (storedUser && storedToken) {
          try {
            const user = JSON.parse(storedUser);
            resolve(user);
            return;
          } catch (error) {
            console.error('Error parsing stored user:', error);
          }
        }
        
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: any, session: any) => {
        if (err || !session.isValid()) {
          // Fallback to localStorage if session is invalid
          const storedUser = localStorage.getItem('authUser');
          const storedToken = localStorage.getItem('authToken');
          
          if (storedUser && storedToken) {
            try {
              const user = JSON.parse(storedUser);
              resolve(user);
              return;
            } catch (error) {
              console.error('Error parsing stored user:', error);
            }
          }
          
          resolve(null);
          return;
        }

        // Use ID token instead of access token - ID token contains email
        const idToken = session.getIdToken();
        const payload = idToken.payload;
        const groups = payload['cognito:groups'] as string[] || [];
        
        const user: AuthUser = {
          email: payload.email || cognitoUser.getUsername(),
          userId: payload.sub,
          groups,
          role: (groups.includes('admin') || groups.includes('admins')) ? 'admin' : 'guest',
        };

        // Update localStorage with fresh data
        localStorage.setItem('authUser', JSON.stringify(user));
        localStorage.setItem('authToken', idToken.getJwtToken());
        
        resolve(user);
      });
    });
  }

  static async forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (error: any) => {
          console.error('Forgot password error:', error);
          
          if (error.code === 'UserNotFoundException') {
            // Don't reveal if user exists or not for security
            resolve(); // Silently succeed
          } else if (error.code === 'LimitExceededException') {
            reject(new Error('Too many requests. Please try again later.'));
          } else {
            reject(new Error(error.message || 'Failed to send reset email'));
          }
        },
      });
    });
  }

  static async resetPassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (error: any) => {
          console.error('Reset password error:', error);
          
          if (error.code === 'CodeMismatchException') {
            reject(new Error('Invalid verification code'));
          } else if (error.code === 'ExpiredCodeException') {
            reject(new Error('Verification code has expired'));
          } else if (error.code === 'InvalidPasswordException') {
            reject(new Error('Password does not meet requirements'));
          } else {
            reject(new Error(error.message || 'Failed to reset password'));
          }
        },
      });
    });
  }

  static async getAuthToken(): Promise<string | null> {
    return new Promise((resolve) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: any, session: any) => {
        if (err || !session.isValid()) {
          resolve(null);
          return;
        }

        // Use ID token instead of access token
        resolve(session.getIdToken().getJwtToken());
      });
    });
  }

  static async completeNewPasswordChallenge(
    cognitoUser: any, 
    newPassword: string, 
    userAttributes: any = {}
  ): Promise<LoginResult> {
    return new Promise((resolve, reject) => {
      // For password challenge, we typically don't need to pass any user attributes
      // since the user already exists with all required attributes
      // Only pass attributes that are specifically required and mutable
      const mutableAttributes = {};
      
      cognitoUser.completeNewPasswordChallenge(newPassword, mutableAttributes, {
        onSuccess: (result: any) => {
          // Use ID token instead of access token - ID token contains email
          const idToken = result.getIdToken();
          const payload = idToken.payload;
          
          // Extract user groups from JWT token
          const groups = payload['cognito:groups'] as string[] || [];
          
          const authUser: AuthUser = {
            email: payload.email || cognitoUser.getUsername(),
            userId: payload.sub,
            groups,
            role: (groups.includes('admin') || groups.includes('admins')) ? 'admin' : 'guest',
          };

          resolve({
            user: authUser,
            token: idToken.getJwtToken(),
          });
        },
        onFailure: (error: any) => {
          console.error('Password change error:', error);
          
          if (error.code === 'InvalidPasswordException') {
            reject(new Error('Password does not meet requirements. Please ensure it has at least 8 characters with uppercase, lowercase, and number.'));
          } else if (error.code === 'InvalidParameterException') {
            reject(new Error('Invalid password format. Please try a different password.'));
          } else {
            reject(new Error(error.message || 'Failed to change password'));
          }
        }
      });
    });
  }

  static async loginWithPin(email: string, pin: string): Promise<LoginResult> {
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL 
        ? `${process.env.REACT_APP_API_BASE_URL}/auth/pin-login`
        : (process.env.NODE_ENV === 'production' ? '/api/auth/pin-login' : 'http://localhost:3001/api/auth/pin-login');
      const result = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, pin }),
      });

      if (!result.ok) {
        const errorData = await result.json().catch(() => ({ message: 'Login failed' }));
        throw new Error(errorData.message || 'Invalid PIN or email');
      }

      const data = await result.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Login failed');
      }

      const authUser: AuthUser = {
        email: data.data.user.email,
        userId: data.data.user.id,
        groups: data.data.user.groups || [],
        role: data.data.user.role || data.data.user.userRole || (data.data.user.groups?.includes('admins') ? 'admin' : 'guest'),
      };

      // Store FULL user info in localStorage for persistence (including fullName, etc.)
      localStorage.setItem('authToken', data.data.token);
      localStorage.setItem('authUser', JSON.stringify(data.data.user)); // Store full user object
      
      // Also store simplified auth user for backward compatibility
      localStorage.setItem('auth', JSON.stringify(authUser));

      return {
        user: data.data.user, // Return full user object
        token: data.data.token,
      };
    } catch (error: any) {
      console.error('PIN login error:', error);
      
      // Handle specific error messages
      if (error.message.includes('Invalid PIN')) {
        throw new Error('Invalid PIN. Please check your 4-digit PIN and try again.');
      } else if (error.message.includes('Account locked')) {
        throw new Error('Account temporarily locked due to multiple failed attempts. Please try again later.');
      } else if (error.message.includes('User not found')) {
        throw new Error('No account found with this email address.');
      } else if (error.message.includes('Too many requests')) {
        throw new Error('Too many login attempts. Please wait a few minutes and try again.');
      } else {
        throw new Error(error.message || 'Login failed. Please try again.');
      }
    }
  }

  static isAuthenticated(): boolean {
    return localStorage.getItem('authToken') !== null;
  }
}

export default AuthService;